import { TFunction } from 'i18next';
import dependencies from '../../dependencies.json';
import { addInVersion, graphApiBaseUrl } from '../env-variables.config';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import durationPlugin from 'dayjs/plugin/duration';
import { InvoiceableType } from './transaction';
import { showToast } from '../components/utils/toast';
import { GENERAL_ENTITY_DISABLED_ID, TasksFilterValues } from './tasks';
import { InvoiceableDurationStep, LOCAL_STORAGE_NAMES, ThemeMode } from './constants';
import { getRegionLocale } from './locale';
import { graphFetch } from '../api/graphClient';
import { ITimer } from '../interface/timer';
import { IRegionalNumberFormat, ITransaction } from '../interface/transaction';
import { Entities } from '../interface/common';

// Required plugins
dayjs.extend(isoWeek);
dayjs.extend(isLeapYear);
dayjs.extend(durationPlugin);

interface IDependencyDetails {
  licenses: string;
  repository?: string;
  publisher: string;
  path: string;
  licenseFile: string;
}

export type QueryParamValue =
  string | number | boolean | null | undefined | QueryParamObject | QueryParamValue[];
export interface QueryParamObject {
  [key: string]: QueryParamValue;
}
interface RecipientField {
  getAsync: (
    callback: (result: { status: string; value: { emailAddress: string }[] }) => void,
  ) => void;
}
export const toQueryString = (obj: QueryParamObject, prefix = ''): string => {
  return Object.keys(obj)
    .map((key) => {
      const value = obj[key];
      const prefixedKey = prefix ? `${prefix}[${key}]` : key;

      if (value === null || value === undefined) return '';
      if (typeof value === 'object' && !Array.isArray(value)) {
        return toQueryString(value, prefixedKey); // Recursively handle nested objects
      }
      if (Array.isArray(value)) {
        return value
          .map((val) => `${encodeURIComponent(prefixedKey)}=${encodeURIComponent(String(val))}`)
          .join('&');
      }

      return `${encodeURIComponent(prefixedKey)}=${encodeURIComponent(String(value))}`;
    })
    .filter(Boolean)
    .join('&');
};
export const getThemeTranslation = (theme: string, t: TFunction) => {
  return getThemeOptions(t).find((option) => option.value === theme.toLowerCase())?.text ?? '';
};
export const getThemeOptions = (t: TFunction) => [
  { text: t('common:profile.theme.light'), value: 'light' },
  { text: t('common:profile.theme.dark'), value: 'dark' },
  { text: t('common:profile.theme.system'), value: 'system' },
];
export const getOpenSourceLibraries = () => {
  return Object.entries(dependencies as unknown as Record<string, IDependencyDetails>)?.map(
    ([name, details]) => ({
      name,
      license: details.licenses,
      repository: details.repository ?? `https://www.npmjs.com/package/${name.split('@')[0]}`,
    }),
  );
};
export const getAddInInformation = (t: TFunction) => {
  if (Office?.context?.mailbox) {
    const officeHost = Office.context.diagnostics.host;
    const officeVersion = Office.context.diagnostics.version;
    const officeBitness = Office.context.diagnostics.platform;
    const osInfo = globalThis.navigator.userAgent;
    return `<p>${t('common:profile.infoForDeveloper')}:</p>
      <ul>
        <li><b>${t('common:profile.officeAddInVersion')}:</b> ${addInVersion}</li>
        <li><b>${t('common:profile.officeAppType')}:</b> ${officeHost}</li>
        <li><b>${t('common:profile.officeAppVersion')}:</b> ${officeVersion}</li>
        <li><b>${t('common:profile.officeVersionBit')}:</b> ${officeBitness}</li>
        <li><b>${t('common:profile.osInfo')}:</b> ${osInfo}</li>
      </ul>`;
  }
  return '';
};
export const formatHourTransactionsDate = (dateStr: string, t: TFunction): string => {
  const date = new Date(dateStr);
  const weekdayKey = `weekCalendar.${date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()}`;
  const weekday = t(`${weekdayKey}`) ?? date.toLocaleDateString('en-US', { weekday: 'short' });

  return `${formatter.dateWithoutYear(date)} ${weekday}`;
};
export const getWeekDateRange = (year: number, weekNumber: number) => {
  const firstDayOfYear = dayjs().year(year).startOf('year');
  const weekStart = firstDayOfYear.isoWeek(weekNumber).startOf('isoWeek'); // Monday start
  const weekEnd = weekStart.clone().endOf('isoWeek'); // Sunday end

  return {
    weekStart: weekStart.format('YYYY-MM-DD'),
    weekEnd: weekEnd.format('YYYY-MM-DD'),
  };
};
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${size} ${sizes[i]}`;
};

export const downloadBase64File = (base64Data: string, filename: string, mimeType: string) => {
  const link = document.createElement('a');
  link.href = `data:${mimeType};base64,${base64Data}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
export const getMatterLabel = (t: TFunction, invoiceableType: string) => {
  let title = t('matter:matter');
  switch (invoiceableType) {
    case InvoiceableType.BILLABLE:
      title = t('matter:billableMatter');
      break;
    case InvoiceableType.NON_BILLABLE:
      title = t('matter:nonBillableMatter');
      break;
    case InvoiceableType.INTERNAL:
      title = t('matter:internalMatter');
      break;
    default:
      break;
  }
  return title;
};
export const getMatterIconClass = (invoiceableType: string): string => {
  switch (invoiceableType) {
    case InvoiceableType.BILLABLE: {
      return 'text-clr-green-200';
    }
    case InvoiceableType.INTERNAL: {
      return 'text-clr-beige-200';
    }
    default: {
      return '';
    }
  }
};
export const hasImportantNotes = (t: TFunction) => {
  showToast('info', t('common:entityHasImportantNotes'));
};
export const getStatusFilterForTasks = (filterValue?: string) => {
  if (filterValue === TasksFilterValues.inComplete) {
    return `$not:${GENERAL_ENTITY_DISABLED_ID}`;
  } else if (filterValue === TasksFilterValues.completed) {
    return `$eq:${GENERAL_ENTITY_DISABLED_ID}`;
  } else {
    return undefined;
  }
};
export const durationStepConverter = (durationStep: InvoiceableDurationStep) => {
  if (durationStep === InvoiceableDurationStep.SIX_MINUTES) return 6;
  else if (durationStep === InvoiceableDurationStep.TWELVE_MINUTES) return 12;
  else if (durationStep === InvoiceableDurationStep.FIFTEEN_MINUTES) return 15;
  else return 30;
};

export const applyRoundingRuleToBillableHours = (
  billableHours: number,
  invoiceableDurationStep: InvoiceableDurationStep,
) => {
  if (invoiceableDurationStep === InvoiceableDurationStep.NONE) {
    return billableHours;
  }
  const stepInHours = durationStepConverter(invoiceableDurationStep) / 60;
  return Number((Math.ceil(billableHours / stepInHours) * stepInHours).toFixed(2));
};
export function getChangedFields<T>(original: T, updated: T): Partial<T> {
  const result: Partial<T> = {};

  for (const key in updated) {
    const updatedValue = updated[key];
    const originalValue = original?.[key];

    if (isObject(updatedValue) && isObject(originalValue)) {
      handleNestedObjectChanges(result, key, originalValue, updatedValue);
    } else if (updatedValue !== originalValue) {
      result[key] = updatedValue;
    }
  }

  return result;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function handleNestedObjectChanges<T>(
  result: Partial<T>,
  key: string,
  originalValue: unknown,
  updatedValue: unknown,
) {
  if (Array.isArray(updatedValue)) {
    if (JSON.stringify(updatedValue) !== JSON.stringify(originalValue)) {
      result[key as keyof T] = updatedValue as T[keyof T];
    }
  } else {
    const nestedChanges = getChangedFields(originalValue, updatedValue);
    if (nestedChanges && Object.keys(nestedChanges).length > 0) {
      result[key as keyof T] = nestedChanges as T[keyof T];
    }
  }
}
const resolveMailboxSubject = (transactionSubject?: string): Promise<string> => {
  const rawSubject = Office.context.mailbox.item?.subject;
  return new Promise<string>((resolve) => {
    const subjectAsObj = rawSubject as unknown as Record<string, unknown>;
    if (rawSubject && typeof subjectAsObj.getAsync === 'function') {
      (subjectAsObj.getAsync as (cb: (r: { status: string; value: string }) => void) => void)(
        (result) => resolve(result.value || transactionSubject || ''),
      );
    } else {
      resolve((rawSubject as string) || transactionSubject || '');
    }
  });
};

const fetchMessageWithAttachments = async (restId: string) => {
  try {
    const response = await graphFetch(`${graphApiBaseUrl}/messages/${restId}?$expand=attachments`);
    return response.ok ? await response.json() : null;
  } catch (error) {
    console.error('Failed to fetch message from Microsoft Graph:', error);
    return null;
  }
};

export const generateEmlWithoutAttachments = async (
  transactionSubject?: string,
): Promise<{
  file: File | null;
  subject: string;
  error?: string;
}> => {
  const itemId = Office.context.mailbox.item?.itemId ?? '';
  const restId = Office.context.mailbox.convertToRestId(
    itemId,
    Office.MailboxEnums.RestVersion.v2_0,
  );

  const message = await fetchMessageWithAttachments(restId);
  if (!message) {
    return {
      error: 'common:emlGenerationAttachmentError',
      file: null,
      subject: '',
    };
  }

  const { body, sender, toRecipients = [], attachments = [] } = message;
  const subject = await resolveMailboxSubject(transactionSubject);

  const from = sender?.emailAddress?.address ?? '';
  const to = toRecipients
    .map((r: { emailAddress: { address: string } }) => r.emailAddress.address)
    .join(', ');
  const htmlBody = body?.content;

  // Prepare inline attachments
  const inlineAttachments: { cid: string; mimeType: string; contentBytes: string }[] = [];

  for (const att of attachments) {
    if (att.isInline && att.contentId && att.contentBytes && att.contentType) {
      // Replace src="cid:xyz" references if needed (optional - Outlook usually handles this already)
      inlineAttachments.push({
        cid: att.contentId,
        mimeType: att.contentType,
        contentBytes: att.contentBytes,
      });
    }
  }

  const boundary = `----=_Part_${Math.random().toString(36).substring(2, 15)}`;
  const crlf = '\r\n';

  const emlParts = [
    `From: ${from}`,
    `To: ${to}`,
    `Subject: ${subject}`,
    `MIME-Version: 1.0`,
    `Content-Type: multipart/related; boundary="${boundary}"`,
    '',
    `--${boundary}`,
    `Content-Type: text/html; charset="UTF-8"`,
    `Content-Transfer-Encoding: 7bit`,
    '',
    htmlBody,
  ];

  // Append inline images
  for (const att of inlineAttachments) {
    emlParts.push(
      '',
      `--${boundary}`,
      `Content-Type: ${att.mimeType}`,
      `Content-Transfer-Encoding: base64`,
      `Content-ID: <${att.cid}>`,
      '',
      att.contentBytes,
    );
  }

  emlParts.push('', `--${boundary}--`, '');

  const eml = emlParts.join(crlf);
  const fileName = sanitizeFilename(`${subject}.eml`);
  const file = new File([eml], fileName, { type: 'message/rfc822' });

  return { file, subject };
};

export const generateEmlFromMail = async (
  transactionSubject?: string,
): Promise<{ file: File; subject: string }> => {
  const mailboxItem = Office.context.mailbox.item;
  const itemId =
    mailboxItem?.itemId ??
    (await new Promise<string>((resolve) => {
      mailboxItem?.getItemIdAsync?.((result) => {
        resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : '');
      });
    }));
  const subject = await resolveMailboxSubject(transactionSubject);
  const restId = Office.context.mailbox.convertToRestId(
    itemId,
    Office.MailboxEnums.RestVersion.v2_0,
  );

  const emlFromMail = await graphFetch(`${graphApiBaseUrl}/messages/${restId}/$value`);
  if (!emlFromMail.ok) {
    throw new Error(`Failed to fetch email content: ${emlFromMail.statusText}`);
  }
  const emlBlob = await emlFromMail.blob();
  const fileName = sanitizeFilename(`${subject}.eml`);
  const file = new File([emlBlob], fileName, {
    type: 'message/rfc822',
  });
  return {
    file,
    subject,
  };
};
interface DurationResult {
  value: string;
  unit: 'minutes' | 'hours';
}

export function calculateDuration(start: string, end: string): DurationResult {
  const startTime = dayjs(start);
  const endTime = dayjs(end);

  if (!startTime.isValid() || !endTime.isValid() || endTime.isBefore(startTime)) {
    return { value: '0', unit: 'minutes' };
  }

  const diffMinutes = endTime.diff(startTime, 'minute');

  if (diffMinutes < 60) {
    return {
      value: diffMinutes.toString(),
      unit: 'minutes',
    };
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  return {
    value: minutes === 0 ? `${hours}` : `${hours}:${minutes.toString().padStart(2, '0')}`,
    unit: 'hours',
  };
}
export const extractDate = (datetime?: string) =>
  datetime ? dayjs(datetime).startOf('day').toDate() : undefined;

export const extractTime = (datetime?: string) => (datetime ? dayjs(datetime).toDate() : undefined);

/**
 * Add forms seed their date fields with the chosen day at midnight, which leaves the
 * time pickers beside them reading as a deliberate 00:00. Reporting such a picker as
 * empty is what lets it open on — and commit — the current time instead. A saved
 * record is taken at face value, where 00:00 is a time someone did choose.
 */
export const chosenTimeOfDay = (datetime?: string, isSavedRecord = false) => {
  if (!datetime) return undefined;
  const time = dayjs(datetime);
  const isSeededDayOnly = !isSavedRecord && time.isSame(time.startOf('day'));
  return isSeededDayOnly ? undefined : time;
};

export const mergeDateAndTime = (date: Date, time: Date) =>
  dayjs(date)
    .hour(dayjs(time).hour())
    .minute(dayjs(time).minute())
    .second(dayjs(time).second())
    .toDate();
export const getItemType = () =>
  Office?.context?.mailbox?.item?.itemType === Office.MailboxEnums.ItemType.Message
    ? 'messages'
    : 'events';

export const formatTime = (seconds: number) => {
  const hrs = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const secs = String(seconds % 60).padStart(2, '0');
  return `${hrs}:${mins}:${secs}`;
};
export const decimalHours = (seconds: number): number => {
  return Math.round((seconds / 3600) * 100) / 100;
};
export const groupTimeSlotsByDate = (timer: ITimer) => {
  const groups: Record<string, ITimer['intervals']> = {};

  timer.intervals?.forEach((slot) => {
    const date = dayjs(slot.start).format('MMM DD, YYYY');
    groups[date] ??= [];
    groups[date].push(slot);
  });

  return groups;
};

export const calculateTimeDuration = (slots: ITimer['intervals']) => {
  return slots?.reduce((total, slot) => {
    const start = dayjs(slot.start);
    const end = dayjs(slot.end);
    return total + end.diff(start, 'second');
  }, 0);
};
export const formatShortDuration = (seconds: number) => {
  const duration = dayjs.duration(seconds, 'seconds');
  const hours = duration.hours();
  const minutes = duration.minutes();

  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
};
export const computeInitialDuration = (timerId: string): number => {
  const timers: ITimer[] = JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAMES.timer) ?? '[]');
  const timer = timers.find((t) => t.id === timerId);
  if (!timer) return 0;

  const baseDuration = timer.duration ?? 0;

  if (timer.isRunning && timer.intervalStart) {
    const start = new Date(timer.intervalStart).getTime();
    const now = Date.now();
    const delta = Math.floor((now - start) / 1000);
    return baseDuration + delta;
  }

  return baseDuration;
};
export const getFormatedOrganisationList = (organisationList: IOrganisation[]) => {
  return (organisationList ?? [])
    .map((item) => ({
      ...item,
      disabled: !item.isOperational,
    }))
    .sort((a, b) => {
      if (a.isOperational !== b.isOperational) {
        return a.isOperational ? -1 : 1;
      }
      return a.organizationName.localeCompare(b.organizationName, undefined, {
        sensitivity: 'base',
      });
    });
};
export const formatter = {
  dateStandard: (time: Date) => {
    const locale = getRegionLocale();
    const formatterData = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
    return formatterData.format(time);
  },
  dateWithoutYear: (time: Date) => {
    const locale = getRegionLocale();
    const formatterData = new Intl.DateTimeFormat(locale, {
      month: 'numeric',
      day: 'numeric',
    });
    return formatterData.format(time);
  },
  regionalNumberFormat: ({ value, minimumFractionDigits = 0 }: IRegionalNumberFormat) => {
    const formatterData = new Intl.NumberFormat(getRegionLocale(), {
      minimumFractionDigits: minimumFractionDigits ?? 0,
    });
    return formatterData.format(value);
  },
};

export const formatDateGroupTitle = (date: dayjs.ConfigType, t: TFunction) =>
  dayjs(date).isSame(dayjs(), 'day')
    ? t('common:today')
    : formatter.dateStandard(dayjs(date).toDate());

export const formatGroupHours = (hours: number, t: TFunction) =>
  `${formatter.regionalNumberFormat({
    value: hours,
    minimumFractionDigits: Number.isInteger(hours) ? 0 : 2,
  })} ${t('common:h')}`;

export const usesCommaDecimalSeparator = (): boolean =>
  formatter.regionalNumberFormat({ value: 1.1 }).includes(',');
export const getUnitPrice = (baseCurrency: string, transaction: ITransaction | undefined) => {
  const rawValue =
    baseCurrency === transaction?.matter?.currency
      ? transaction?.unitPrice
      : transaction?.unitPriceInCurrency;

  return formatter.regionalNumberFormat({
    value: rawValue ?? 0,
    minimumFractionDigits: 2,
  });
};

export const getTotalPrice = (baseCurrency: string, transaction: ITransaction | undefined) => {
  const value =
    baseCurrency === transaction?.matter?.currency
      ? transaction?.totalPrice
      : transaction?.totalPriceInCurrency;
  return formatter.regionalNumberFormat({
    value: value ?? 0,
    minimumFractionDigits: 2,
  });
};
export const getDisplayKey = (dateKey: string, iterDate: dayjs.Dayjs, t: TFunction) => {
  return dateKey === dayjs().format('YYYY-MM-DD')
    ? t('common:today')
    : dayjs(iterDate).format('MMM DD, YYYY');
};

export const groupTransactionsByDate = (
  transactions: ITransaction[],
  t: TFunction,
): Record<string, { items: ITransaction[]; date: Date }> => {
  return transactions.reduce(
    (acc, curr) => {
      const date = dayjs(curr.transactionDate).isSame(dayjs(), 'day')
        ? t('common:today')
        : dayjs(curr.transactionDate).format('MMM DD, YYYY');
      if (!acc[date]) {
        acc[date] = {
          date: new Date(date),
          items: [],
        };
      }
      acc[date].items.push(curr);
      return acc;
    },
    {} as Record<string, { items: ITransaction[]; date: Date }>,
  );
};
const MAX_PROFILE_PICTURE_SIZE_MB = 1;

export const getProfilePictureValidationError = (file: File, t: TFunction): string | null => {
  if (!file.type.startsWith('image/')) {
    return t('common:profile.uploadPhotoImageValidation');
  }

  if (file.size / 1024 / 1024 >= MAX_PROFILE_PICTURE_SIZE_MB) {
    return t('common:profile.uploadPhotoSizeValidation');
  }

  return null;
};
export const displayFullCountryName = (
  countryCode: string,
  appLanguage: string,
  countryNames: Intl.DisplayNames = new Intl.DisplayNames([appLanguage], { type: 'region' }),
) => countryNames.of(countryCode);
export const sanitizeFilename = (filename: string): string => {
  // Separate name and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const namePart = lastDotIndex === -1 ? filename : filename.slice(0, lastDotIndex);
  const extPart = lastDotIndex === -1 ? '' : filename.slice(lastDotIndex);

  // 1. Remove emojis
  const withoutEmojis = namePart.replace(/[\p{Extended_Pictographic}]/gu, '');

  // 2. Remove all unsafe characters except letters, numbers, space, dot, dash, underscore
  const cleaned = withoutEmojis.replace(/[^a-zA-Z0-9 ._-]/g, '');

  // 3. Trim extra whitespace
  const trimmed = cleaned.trim().replace(/\s+/g, ' ');

  // 4. Truncate to fit filename limit
  const maxNameLength = 255 - extPart.length;
  const safeName = trimmed.slice(0, maxNameLength);

  return safeName + extPart;
};
export const displayLanguageName = (
  language: string,
  appLanguage?: string,
  languageNames: Intl.DisplayNames = new Intl.DisplayNames([appLanguage ?? language], {
    type: 'language',
  }),
) => {
  let languageName = '';

  if (language.length > 4) {
    languageName = languageNames.of(language.slice(0, -3)) ?? '';
  } else {
    languageName = languageNames.of(language) ?? '';
  }

  return languageName.charAt(0).toUpperCase() + languageName.slice(1);
};

export const rgbToRgba = (rgb: string, opacity: number) => {
  // Extract the rgb values
  const rgbValues = rgb.match(/\d+/g);

  if (!rgbValues) {
    return null;
  }

  const [r, g, b] = rgbValues.map(Number);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const workTimeFallBackColor = (mode: ThemeMode) =>
  mode === ThemeMode.dark ? 'rgb(221, 236, 254)' : 'rgb(140, 160, 180)';

export const getEntityNotFoundMessage = (entity: string, t: TFunction): string => {
  switch (entity) {
    case Entities.ACTIVITY:
      return t('task:doesNotExist');
    case Entities.MATTER:
      return t('matter:doesNotExist');
    case Entities.TRANSACTION:
      return t('transaction:doesNotExist');
    case Entities.CUSTOMER:
      return t('customer:doesNotExist');
    case Entities.CONFLICTCHECKS:
      return t('conflict:conflictCheckDoesNotExist');
    case Entities.PRELIMINARYINVOICE:
      return t('preliminaryInvoice:doesNotExist');
    default:
      return t('common:somethingWentWrong');
  }
};

function hasGetAsync(obj: unknown): obj is RecipientField {
  return !!obj && typeof (obj as RecipientField).getAsync === 'function';
}
const removeCurrentUser = (email: string) => {
  const currentUserEmail = Office.context.mailbox.userProfile.emailAddress;
  return email !== currentUserEmail;
};
const parseForwardedAddresses = (bodyText: string) => {
  // Regex to match all "From:" and "To:" lines (case-insensitive, global)
  const fromRegex = /^From:\s*([^\r\n<]+<([^>]+)>|[^\r\n]+)$/gim;
  const toRegex = /^To:\s*([^\r\n<]+<([^>]+)>|[^\r\n]+)$/gim;

  const fromEmails: string[] = [];
  const toEmails: string[] = [];

  let match: RegExpExecArray | null;

  // Find all "From:" addresses
  while ((match = fromRegex.exec(bodyText)) !== null) {
    let email = match[2] ? match[2] : match[1];
    email = email
      .replace(/.*<|>/g, '')
      .replace(/^mailto:/i, '')
      .trim();
    if (email && !fromEmails.includes(email)) {
      fromEmails.push(email);
    }
  }

  // Find all "To:" addresses (may be multiple per line, separated by ;)
  while ((match = toRegex.exec(bodyText)) !== null) {
    const emails = match[1]
      .split(';')
      .map((s) =>
        s
          .replace(/.*<|>/g, '')
          .replace(/^mailto:/i, '')
          .trim(),
      )
      .filter(Boolean);
    emails.forEach((email) => {
      if (email && !toEmails.includes(email)) {
        toEmails.push(email);
      }
    });
  }

  return {
    fromEmails,
    toEmails,
  };
};
export const getAllMailRecipients = (emailFolderName?: string): Promise<string[]> => {
  return new Promise((resolve) => {
    const item = Office.context.mailbox.item;
    const recipients: string[] = [];
    let toDone = false,
      fromDone = false,
      organizerDone = false,
      requiredDone = false,
      optionalDone = false;

    const extractEmails = (arr: { emailAddress: string }[]) =>
      arr.filter((r) => r && typeof r.emailAddress === 'string').map((r) => r.emailAddress);

    const checkDone = () => {
      if (fromDone && toDone) {
        const uniqueRecipients = Array.from(new Set(recipients));
        const removeCurrentOutlookUser = uniqueRecipients.filter(removeCurrentUser);

        // Try to parse forwarded addresses if not already present
        if (item?.body?.getAsync) {
          item.body.getAsync(Office.CoercionType.Text, (result) => {
            if (
              result.status === Office.AsyncResultStatus.Succeeded &&
              typeof result.value === 'string'
            ) {
              const { fromEmails, toEmails } = parseForwardedAddresses(result.value);
              fromEmails.forEach((email) => {
                if (email && !removeCurrentOutlookUser.includes(email)) {
                  removeCurrentOutlookUser.push(email);
                }
              });
              toEmails.forEach((email) => {
                if (email && !removeCurrentOutlookUser.includes(email)) {
                  removeCurrentOutlookUser.push(email);
                }
              });
            }
            resolve(removeCurrentOutlookUser.filter(removeCurrentUser));
          });
        } else {
          resolve(removeCurrentOutlookUser.filter(removeCurrentUser));
        }
      }
    };

    const checkAppointmentDone = () => {
      if (organizerDone && requiredDone && optionalDone) {
        const uniqueRecipients = Array.from(new Set(recipients));
        const removeCurrentOutlookUser = uniqueRecipients.filter(removeCurrentUser);
        resolve(removeCurrentOutlookUser);
      }
    };

    const itemType = item?.itemType || Office.context.mailbox.item?.itemType;
    if (itemType === Office.MailboxEnums.ItemType.Message) {
      let fromEmail: string | undefined;
      if (item?.from) {
        if (typeof item.from.getAsync === 'function') {
          item.from.getAsync((result) => {
            if (
              result.status === Office.AsyncResultStatus.Succeeded &&
              result.value &&
              typeof result.value.emailAddress === 'string'
            ) {
              fromEmail = result.value.emailAddress;
              recipients.push(fromEmail);
            }
            fromDone = true;
            checkDone();
          });
        } else if (item.from.emailAddress) {
          fromEmail = item.from.emailAddress;
          recipients.push(fromEmail);
          fromDone = true;
          checkDone();
        } else {
          fromDone = true;
          checkDone();
        }
      } else {
        fromDone = true;
        checkDone();
      }

      if (Array.isArray(item?.to)) {
        const extractToEmails = extractEmails(item.to);
        if (emailFolderName === 'Inbox') {
          recipients.push(...extractToEmails.filter(removeCurrentUser));
        } else {
          recipients.push(...extractToEmails);
        }
        toDone = true;
        checkDone();
      } else if (hasGetAsync(item?.to)) {
        (item.to as Office.Recipients).getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded && Array.isArray(result.value)) {
            recipients.push(...extractEmails(result.value));
          }
          toDone = true;
          checkDone();
        });
      } else {
        toDone = true;
        checkDone();
      }
    } else if (itemType === Office.MailboxEnums.ItemType.Appointment) {
      // --- Appointment/Calendar ---
      // ORGANIZER
      if (item?.organizer) {
        if (typeof item.organizer.getAsync === 'function') {
          item.organizer.getAsync((result) => {
            if (
              result.status === Office.AsyncResultStatus.Succeeded &&
              result.value &&
              typeof result.value.emailAddress === 'string'
            ) {
              recipients.push(result.value.emailAddress);
            }
            organizerDone = true;
            checkAppointmentDone();
          });
        } else if (item.organizer.emailAddress) {
          recipients.push(item.organizer.emailAddress);
          organizerDone = true;
          checkAppointmentDone();
        } else {
          organizerDone = true;
          checkAppointmentDone();
        }
      } else {
        organizerDone = true;
        checkAppointmentDone();
      }

      // REQUIRED ATTENDEES
      if (Array.isArray(item?.requiredAttendees)) {
        recipients.push(...extractEmails(item.requiredAttendees));
        requiredDone = true;
        checkAppointmentDone();
      } else if (hasGetAsync(item?.requiredAttendees)) {
        (item.requiredAttendees as Office.Recipients).getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded && Array.isArray(result.value)) {
            recipients.push(...extractEmails(result.value));
          }
          requiredDone = true;
          checkAppointmentDone();
        });
      } else {
        requiredDone = true;
        checkAppointmentDone();
      }

      // OPTIONAL ATTENDEES
      if (Array.isArray(item?.optionalAttendees)) {
        recipients.push(...extractEmails(item.optionalAttendees));
        optionalDone = true;
        checkAppointmentDone();
      } else if (hasGetAsync(item?.optionalAttendees)) {
        (item.optionalAttendees as Office.Recipients).getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded && Array.isArray(result.value)) {
            recipients.push(...extractEmails(result.value));
          }
          optionalDone = true;
          checkAppointmentDone();
        });
      } else {
        optionalDone = true;
        checkAppointmentDone();
      }
    } else {
      // Unknown item type, resolve empty
      resolve([]);
    }
  });
};
export function areStringsEqual(a?: string, b?: string): boolean {
  if (!a || !b) {
    return false;
  }

  return a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0 ? true : false;
}
export function getItemField<T = unknown>(
  item: Record<string, unknown> | undefined,
  field: string,
): Promise<T | undefined> {
  return new Promise((resolve) => {
    const fieldObj = item?.[field];
    if (fieldObj && typeof (fieldObj as { getAsync?: unknown }).getAsync === 'function') {
      (fieldObj as { getAsync: (cb: (result: Office.AsyncResult<T>) => void) => void }).getAsync(
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            resolve(undefined);
          }
        },
      );
    } else {
      resolve(fieldObj as T | undefined);
    }
  });
}
export const getCookie = (name: string): string | undefined => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || undefined;
  return undefined;
};
export const getLocalDateAndTime = (date?: Date, onlyDate?: boolean) => {
  if (!date || !dayjs(date).isValid()) return '';
  if (onlyDate) {
    return `${formatter.dateStandard(date)}`;
  }
  return `${formatter.dateStandard(date)}, ${dayjs(date).format('HH:mm')}`;
};
export const formatDateRange = (startDate?: string, endDate?: string) =>
  startDate && endDate
    ? `${getLocalDateAndTime(new Date(startDate), true)} - ${getLocalDateAndTime(new Date(endDate), true)}`
    : '-';
